import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, fetchLiveGame } from "../../api";
import type { ActiveGame } from "../../types";
import { mmss, useSecondsUntil } from "../../utils/time";
import { InGameCard } from "../InGameCard";
import { button, Skeleton } from "../ui/primitives";

const POLL_MS = 30_000;

/** Live game for the summoner. Polls every 30 s while the tab is open (the server caches it for 20 s). */
export function LiveTab({ region, nameTag }: { region: string; nameTag: string }) {
	const [game, setGame] = useState<ActiveGame | null | undefined>(undefined); // undefined = loading
	const [error, setError] = useState<string | null>(null);
	const [checking, setChecking] = useState(false);
	const [nextAt, setNextAt] = useState<number | null>(null);
	const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
	const alive = useRef(true);

	const load = useCallback(async () => {
		clearTimeout(timer.current);
		setChecking(true);
		try {
			const g = await fetchLiveGame(region, nameTag);
			if (!alive.current) return;
			setGame(g);
			setError(null);
		} catch (e) {
			if (alive.current) setError(e instanceof ApiError ? e.message : "Could not load the live game");
		} finally {
			if (alive.current) {
				setChecking(false);
				setNextAt(Date.now() + POLL_MS);
				timer.current = setTimeout(load, POLL_MS);
			}
		}
	}, [region, nameTag]);

	useEffect(() => {
		alive.current = true;
		setGame(undefined);
		load();
		return () => {
			alive.current = false;
			clearTimeout(timer.current);
		};
	}, [load]);

	const left = useSecondsUntil(nextAt);

	if (game === undefined && !error) return <LiveSkeleton />;
	if (error && !game) {
		return (
			<div role="alert" className="rounded-xl border border-line bg-surface px-5 py-10 text-center">
				<div className="mb-1.5 text-[17px] font-bold">Couldn&apos;t check for a live game</div>
				<div className="text-sm text-ink-2">
					{error}. We&apos;ll try again in <span className="font-mono text-ink">{mmss(left)}</span>.
				</div>
				<button type="button" onClick={load} disabled={checking} className={button.secondary + " mt-4"}>
					{checking ? "Checking…" : "Try again"}
				</button>
			</div>
		);
	}
	if (game === null) {
		return (
			<div className="rounded-xl border border-line bg-surface px-5 py-14 text-center" aria-live="polite">
				<div aria-hidden="true" className="mx-auto mb-3.5 flex h-11 w-11 items-center justify-center rounded-full border-[1.5px] border-line-2">
					<span className="h-2 w-2 rounded-full bg-line-3" />
				</div>
				<div className="mb-1.5 text-[17px] font-bold">Not in a game right now</div>
				<div className="text-sm text-ink-2">
					Next check in <span className="font-mono text-ink">{mmss(left)}</span>. This page checks every 30 seconds.
				</div>
				<button type="button" onClick={load} disabled={checking} className={button.secondary + " mt-4"}>
					{checking ? "Checking…" : "Check now"}
				</button>
			</div>
		);
	}
	return <InGameCard game={game!} />;
}

function LiveSkeleton() {
	return (
		<div aria-busy="true" aria-label="Checking for a live game" className="flex flex-wrap gap-3.5">
			{[0, 1].map(c => (
				<div key={c} className="flex flex-[1_1_320px] flex-col gap-1.5">
					<Skeleton className="h-7 w-[120px]" />
					{Array.from({ length: 5 }, (_, i) => (
						<Skeleton key={i} className="h-14 rounded-lg" />
					))}
				</div>
			))}
		</div>
	);
}
